import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import { axios } from "@via/api/axios.tsx";
import { NOTIFICATION } from "@via/context/notifications-context.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import {
  buildFiltersQueryString,
  FilterItem,
} from "@via/pages/account/admin/utils/helper.ts";
import { ROUTES } from "@via/utils/constants";
import { downloadCSV, getErrorResponseMessage } from "@via/utils/helpers";
import { ErrorNotification } from "@via/utils/interfaces.tsx";
import {
  AcceptInvitationQueryParams,
  AcceptInvitationResponse,
  CheckInvitationRequestQuery,
  CheckInvitationResponseBody,
  AttachCustomerBoundaryResponseBody,
  ChangeUserPasswordRequestBody,
  CreateUserRequestBody,
  DeleteCustomerBoundaryRequestParams,
  DeleteCustomerBoundaryRequestQuery,
  GlobalFeatureFlagsResponse,
  InsertCustomerBoundaryRequestBody,
  ListCustomerBoundaryResponse,
  ListUsersResponse,
  OrderBy,
  UpdateCustomerBoundaryRequestBody,
  UpdateCustomerBoundaryRequestParams,
  UpdateUser,
  SendTestEmailRequestBody,
  AuditHealthResponse,
  User,
} from "common";
import { useNavigate } from "react-router-dom";

const fetchUsers = async (params: {
  take: number;
  skip: number;
  sort?: string;
  order?: OrderBy;
  filter?: FilterItem[];
}): Promise<ListUsersResponse> => {
  try {
    const baseUrl = "/api/user/v1/users";
    const queryParams = new URLSearchParams({
      take: `${params.take}`,
      skip: `${params.skip}`,
    });
    if (params.sort) {
      queryParams.append("sort", params.sort);
    }
    if (params.order) {
      queryParams.append("order", params.order);
    }
    if (params.filter && params.filter.length > 0) {
      const queryFilter = buildFiltersQueryString(params.filter);
      queryParams.append("filter", queryFilter);
    }
    const url = `${baseUrl}?${queryParams.toString()}`;

    const res = await axios.get(url);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const fetchUsersForExport = async (params: {
  sort?: string;
  order?: OrderBy;
  filter?: FilterItem[];
}): Promise<User[]> => {
  try {
    const baseUrl = "/api/user/v1/users/export";
    const queryParams = new URLSearchParams();
    if (params.sort) {
      queryParams.append("sort", params.sort);
    }
    if (params.order) {
      queryParams.append("order", params.order);
    }
    if (params.filter && params.filter.length > 0) {
      const queryFilter = buildFiltersQueryString(params.filter);
      queryParams.append("filter", queryFilter);
    }
    const url = queryParams.toString()
      ? `${baseUrl}?${queryParams.toString()}`
      : baseUrl;
    const res = await axios.get(url);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

type ExportUsersPayload = {
  sort?: string;
  order?: OrderBy;
  filter?: FilterItem[];
  columns: Array<ColumnDef<User>>;
};

export const useExportUsersForCsv = () => {
  const { setNotification } = useNotifications();

  return useMutation<User[], ErrorNotification, ExportUsersPayload>({
    mutationFn: async ({ sort, order, filter }) =>
      fetchUsersForExport({ sort, order, filter }),
    onSuccess: (usersForExport, variables) => {
      downloadCSV(variables.columns, usersForExport);
      setNotification({
        id: "export-users-success",
        message: "Users exported",
        details: `${usersForExport.length} users exported to CSV.`,
        variant: NOTIFICATION.SUCCESS,
      });
    },
    onError: (error: ErrorNotification | any) => {
      setNotification({
        id: "export-users-error",
        message: "Failed to export users",
        details: error?.response?.data?.message ?? "Failed to export users",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useGetUsers = (params: {
  take: number;
  skip: number;
  sort?: string;
  order?: OrderBy;
  filter?: FilterItem[];
}): UseQueryResult<ListUsersResponse, Error> => {
  return useQuery<ListUsersResponse, Error>({
    queryKey: ["/api/user/v1/users", params],
    queryFn: () => fetchUsers(params),
    retry: true,
  });
};

export const useGetFeatureFlagsByUserId = () => {
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async (payload: { userId: number }) => {
      const url = `/api/admin/feature-flags/v1/${payload.userId}`;

      const res = await axios.get(url);
      return res.data;
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "get-feature-flags-by-user-id",
        message: "Failed to fetch feature flags",
        details:
          getErrorResponseMessage(error) ?? "Failed to fetch feature flags",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useDeleteUserByUserId = () => {
  const queryClient = useQueryClient();
  const { setNotification } = useNotifications();

  return useMutation<void, ErrorNotification, { userId: number }>({
    mutationKey: ["delete-user-by-id"],
    mutationFn: async ({ userId }) => {
      const url = `/api/user/v1/users/${userId}`;
      const { data } = await axios.post(url, { deleted: true });
      return data;
    },
    onSuccess: () => {
      setNotification({
        id: "delete-user-success",
        message: "User deleted",
        details: "The user has been permanently removed.",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/user/v1/users"],
        refetchType: "all",
      });
    },
    onError: (error: ErrorNotification | any) => {
      setNotification({
        id: "delete-user-error",
        message: "Failed to delete user",
        details: error?.response?.data?.message ?? "Failed to delete user",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useUpdateUserByUserId = () => {
  const queryClient = useQueryClient();
  const { setNotification } = useNotifications();

  return useMutation({
    mutationFn: async (payload: { userId: number; params: UpdateUser }) => {
      const url = `/api/user/v1/users/${payload.userId}`;

      const res = await axios.post(url, payload.params);
      return res.data;
    },
    onSuccess: () => {
      setNotification({
        id: "post-user-update-success",
        message: "User Successfully Updated",
        details: "User has been updated",
        variant: NOTIFICATION.SUCCESS,
      });

      queryClient.invalidateQueries({
        queryKey: ["/api/user/v1/users"],
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/auth/v1/user"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "post-user-update-error",
        message: "Failed to update user",
        details: getErrorResponseMessage(error) ?? "Failed to update user",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useCreateUser = () => {
  const queryClient = useQueryClient();
  const { setNotification } = useNotifications();

  return useMutation({
    mutationFn: async (payload: CreateUserRequestBody) => {
      const url = "/api/user/v1/create-user";

      const res = await axios.post(url, payload);
      return res.data;
    },
    onSuccess: () => {
      setNotification({
        id: "post-user-create-success",
        message: "User Successfully Created",
        details: "User has been created",
        variant: NOTIFICATION.SUCCESS,
      });

      queryClient.invalidateQueries({
        queryKey: ["/api/user/v1/users"],
        refetchType: "all",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "post-user-create-error",
        message: "Failed to create user",
        details: getErrorResponseMessage(error) ?? "Failed to create user",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};
export const useAdminChangePassword = (): UseMutationResult<
  unknown,
  ErrorNotification,
  ChangeUserPasswordRequestBody
> => {
  const { setNotification } = useNotifications();

  return useMutation({
    mutationFn: async (payload: ChangeUserPasswordRequestBody) => {
      const url = "/api/auth/v1/change-password";
      const res = await axios.post(url, payload);
      return res.data;
    },
    onSuccess: () => {
      setNotification({
        id: "admin-change-password-success",
        message: "Password updated",
        details: "Password has been updated",
        variant: NOTIFICATION.SUCCESS,
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "admin-change-password-error",
        message: "Failed to update password",
        details:
          error?.response?.data?.message || "Password could not be updated",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};
export const useUpdateFeatureFlagsByUserId = () => {
  const queryClient = useQueryClient();
  const { setNotification } = useNotifications();

  return useMutation({
    mutationFn: async (payload: { userId: number; updatedFlags: string[] }) => {
      const url = `/api/admin/feature-flags/v1/${payload.userId}`;

      const res = await axios.post(url, [...payload.updatedFlags]);
      return res.data;
    },
    onSuccess: () => {
      setNotification({
        id: "post-feature-flags-by-user-id-success",
        message: "Feature Flags Successfully Updated",
        details: "Feature Flags has been updated",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/user/v1/users"],
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/auth/v1/status"],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/auth/v1/user"],
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/configurations/v1/user"],
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/user/v1/service-tokens"],
        refetchType: "all",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "post-feature-flags-by-user-id-error",
        message: "Failed to update feature flags",
        details:
          getErrorResponseMessage(error) ?? "Failed to update feature flags",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

const fetchGlobalFeatureFlags = async (): Promise<
  GlobalFeatureFlagsResponse[]
> => {
  try {
    const res = await axios.get("/api/admin/feature-flags/v1/globally-enabled");
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useGetGlobalFeatureFlags = (): UseQueryResult<
  GlobalFeatureFlagsResponse[],
  ErrorNotification
> => {
  return useQuery<GlobalFeatureFlagsResponse[], ErrorNotification>({
    queryKey: ["/api/admin/feature-flags/v1/globally-enabled"],
    queryFn: () => fetchGlobalFeatureFlags(),
    retry: true,
  });
};

export const useSendTestEmail = () => {
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation({
    mutationFn: async (payload: SendTestEmailRequestBody) => {
      const res = await axios.post("/api/admin/health-check/send-test-email", {
        toEmail: payload.toEmail,
        ...(payload.subject !== undefined && { subject: payload.subject }),
        ...(payload.body !== undefined && { body: payload.body }),
      });
      return res.data;
    },
    onSuccess: () => {
      setNotification({
        id: "send-test-email-success",
        message: "Test email sent",
        details: "Check the recipient inbox to confirm delivery.",
        variant: NOTIFICATION.SUCCESS,
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "send-test-email-error",
        message: "Failed to send test email",
        details: error.response?.data?.message ?? "Failed to send test email",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

const AUDIT_HEALTH_QUERY = ["/api/admin/health-check/audit"];

export const useAuditHealth = () =>
  useQuery<AuditHealthResponse, ErrorNotification>({
    queryKey: AUDIT_HEALTH_QUERY,
    queryFn: async () =>
      (await axios.get("/api/admin/health-check/audit")).data,
  });

/** Verify the audit chain now; the answer replaces the cached audit health. */
export const useVerifyAuditChain = () => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<AuditHealthResponse, ErrorNotification>({
    mutationFn: async () =>
      (await axios.post("/api/admin/health-check/audit/verify")).data,
    onSuccess: data => queryClient.setQueryData(AUDIT_HEALTH_QUERY, data),
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "verify-audit-chain-error",
        message: "Could not verify the audit records",
        details:
          error.response?.data?.message ?? "The verification request failed",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const fetchCustomerBoundary = async (
  take: number,
  skip: number,
  sort: Array<{ id: string; desc: boolean }>,
  filter?: string
): Promise<ListCustomerBoundaryResponse> => {
  try {
    const baseUrl = `/api/admin/customer-boundary`;
    const queryParams = new URLSearchParams({
      take: `${take}`,
      skip: `${skip}`,
    });

    if (sort.length > 0) {
      queryParams.append("sort", sort[0].id);
      queryParams.append("order", sort[0].desc ? "desc" : "asc");
    }

    if (filter) {
      queryParams.append("filter", filter);
    }

    const url = `${baseUrl}?${queryParams.toString()}`;

    const res = await axios.get(url);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useGetCustomerBoundary = (
  page: number = 0,
  pageSize: number = 10,
  sort: Array<{ id: string; desc: boolean }> = [],
  filter: string = ""
): UseQueryResult<ListCustomerBoundaryResponse, Error> => {
  const skip = page * pageSize;
  const take = pageSize;

  return useQuery<ListCustomerBoundaryResponse, Error>({
    // The filter belongs in the key as much as the paging does. The boundary list is filtered per
    // cloud vendor, and the endpoint's COUNT(*) OVER() total is filtered with it, so two vendors
    // sharing one cache entry would serve one vendor's rows and total under the other's tab.
    queryKey: [`/api/admin/customer-boundary`, take, skip, sort, filter],
    queryFn: async () => await fetchCustomerBoundary(take, skip, sort, filter),
    retry: false,
  });
};

export const useAddCustomerBoundary = () => {
  const queryClient = useQueryClient();
  const { setNotification } = useNotifications();

  return useMutation({
    mutationFn: async (payload: InsertCustomerBoundaryRequestBody) => {
      const url = `/api/admin/customer-boundary/upload`;

      const res = await axios.post(url, payload);
      return res.data;
    },
    onSuccess: () => {
      setNotification({
        id: "post-customer-boundary-success",
        message: "Custom Boundary Successfully Created",
        details: "Custom Boundary has been created",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/customer-boundary"],
        refetchType: "all",
      });
    },
    onError: (error: ErrorNotification) => {
      return error;
    },
  });
};

export const useDeleteCustomerBoundary = (): UseMutationResult<
  void,
  ErrorNotification,
  DeleteCustomerBoundaryRequestQuery & DeleteCustomerBoundaryRequestParams
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation<
    void,
    ErrorNotification,
    DeleteCustomerBoundaryRequestQuery & DeleteCustomerBoundaryRequestParams
  >({
    mutationFn: async ({
      id,
      hardDelete,
    }: DeleteCustomerBoundaryRequestQuery &
      DeleteCustomerBoundaryRequestParams) => {
      const url = hardDelete
        ? `/api/admin/customer-boundary/${id}?hardDelete=true`
        : `/api/admin/customer-boundary/${id}`;
      const res = await axios.delete(url);
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "customer-boundary-successfully-deleted",
        message: "Customer Boundary Successfully Deleted",
        details: "This action cannot be undone",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/customer-boundary"],
        refetchType: "all",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "customer-boundary-deletion-failed",
        message: "Customer Boundary Deletion Failed",
        details:
          error?.response?.data?.message ||
          "Contact support if the issue persists",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

/**
 * Attaches a chosen boundary to a chosen organization.
 *
 * Distinct from the pool claim, which happens on its own at launch and picks whatever row is free.
 * This is the deliberate assignment an operator makes, and it is what lets one installation serve
 * more than one cloud.
 */
export const useAttachCustomerBoundary = (): UseMutationResult<
  AttachCustomerBoundaryResponseBody,
  ErrorNotification,
  { customerBoundaryId: string; organizationId: string }
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation<
    AttachCustomerBoundaryResponseBody,
    ErrorNotification,
    { customerBoundaryId: string; organizationId: string }
  >({
    mutationFn: async ({ customerBoundaryId, organizationId }) => {
      const url = `/api/admin/customer-boundary/${customerBoundaryId}/organization`;
      const res = await axios.post(url, { organizationId });
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "customer-boundary-successfully-attached",
        message: "Customer Boundary Attached",
        details: "The organization can now run against this boundary",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/customer-boundary"],
        refetchType: "all",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "customer-boundary-attach-failed",
        message: "Customer Boundary Attach Failed",
        details:
          error?.response?.data?.message ||
          "Contact support if the issue persists",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

/**
 * Revokes an attachment.
 *
 * ⚠️ Addressed by the ATTACHMENT's id, not the boundary's. The two are different rows, and the
 * boundary id here would revoke nothing.
 */
export const useDetachCustomerBoundary = (): UseMutationResult<
  void,
  ErrorNotification,
  { organizationCustomerBoundaryId: string }
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation<
    void,
    ErrorNotification,
    { organizationCustomerBoundaryId: string }
  >({
    mutationFn: async ({ organizationCustomerBoundaryId }) => {
      const url = `/api/admin/customer-boundary/organization/${organizationCustomerBoundaryId}`;
      const res = await axios.delete(url);
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "customer-boundary-successfully-detached",
        message: "Customer Boundary Detached",
        details: "The attachment has been revoked",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/customer-boundary"],
        refetchType: "all",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "customer-boundary-detach-failed",
        message: "Customer Boundary Detach Failed",
        details:
          error?.response?.data?.message ||
          "Contact support if the issue persists",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useEditCustomBoundary = (): UseMutationResult<
  void,
  ErrorNotification,
  UpdateCustomerBoundaryRequestParams & UpdateCustomerBoundaryRequestBody
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<
    void,
    ErrorNotification,
    UpdateCustomerBoundaryRequestParams & UpdateCustomerBoundaryRequestBody
  >({
    mutationFn: async (
      payload: UpdateCustomerBoundaryRequestParams &
        UpdateCustomerBoundaryRequestBody
    ) => {
      const { id, ...body } = payload;
      const res = await axios.put(`/api/admin/customer-boundary/${id}`, body);
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "customer-boundary-successfully-updated",
        message: "Customer Boundary Successfully Updated",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/admin/customer-boundary"],
        refetchType: "all",
      });
    },
    onError: error => {
      return error;
    },
  });
};

const fetchUserInvitationCheck = async (
  token: CheckInvitationRequestQuery
): Promise<CheckInvitationResponseBody | null> => {
  try {
    if (token) {
      const res = await axios.get(
        `/api/user/v1/invitation/check?token=${token?.token}`
      );
      return res.data;
    }
    return null;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useGetUserInvitationCheck = (
  token: CheckInvitationRequestQuery
): UseQueryResult<CheckInvitationResponseBody | null, Error> => {
  return useQuery<CheckInvitationResponseBody | null, Error>({
    queryKey: ["/api/user/v1/invitation/check", token],
    queryFn: () => fetchUserInvitationCheck(token),
    retry: false,
  });
};

export const useAcceptUserInvitation = (): UseMutationResult<
  AcceptInvitationResponse,
  ErrorNotification,
  AcceptInvitationQueryParams
> => {
  const { setNotification, NOTIFICATION } = useNotifications();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation<
    AcceptInvitationResponse,
    ErrorNotification,
    AcceptInvitationQueryParams
  >({
    mutationFn: async (payload: AcceptInvitationQueryParams) => {
      const res = await axios.get(
        `/api/user/v1/invitation/accept?token=${payload.token}`
      );
      return res.data;
    },
    onSuccess: async (response): Promise<void> => {
      setNotification({
        id: "user-invitation-accepted",
        message: "Invitation Accepted",
        details: "You have successfully accepted the invitation.",
        variant: NOTIFICATION.SUCCESS,
      });

      // Invalidate and wait for user query to be refetched
      await queryClient.invalidateQueries({
        queryKey: ["/api/auth/v1/status"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["/api/auth/v1/user"],
      });
      if (response?.sharedContentRelativePath) {
        navigate(`/${response.sharedContentRelativePath}`);
      } else {
        navigate(ROUTES.ACTIVITY);
      }
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "user-invitation-accept-failed",
        message: "Invitation Accept Failed",
        details:
          error?.response?.data?.message ||
          "Failed to accept invitation. Please try again.",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

export const useDeleteInvitation = (): UseMutationResult<
  void,
  ErrorNotification,
  { id: number }
> => {
  const { setNotification, NOTIFICATION } = useNotifications();
  const queryClient = useQueryClient();
  return useMutation<void, ErrorNotification, { id: number }>({
    mutationFn: async (payload: { id: number }) => {
      const res = await axios.delete(`/api/v1/user/invitation/${payload.id}`);
      return res.data;
    },
    onSuccess: async () => {
      setNotification({
        id: "user-invitation-deleted",
        message: "Invitation Deleted",
        details: "You have successfully deleted the invitation.",
        variant: NOTIFICATION.SUCCESS,
      });

      // Invalidate and wait for user query to be refetched
      await queryClient.invalidateQueries({
        queryKey: ["/api/user/v1/user-group"],
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "user-invitation-delete-failed",
        message: "Invitation Delete Failed",
        details:
          error?.response?.data?.message ||
          "Failed to delete invitation. Please try again.",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};
